import { PaintCommand } from '../core/EditorActions.js';

/**
 * Scatter tool — randomly distributes selected tile(s) within a dragged zone.
 *
 * Workflow:
 *   1. Select a tile in the palette (e.g., a flower)
 *   2. Switch to Scatter tool
 *   3. Drag to define a rectangular zone
 *   4. On release, tiles are randomly placed within the zone at the configured density
 *
 * Respects:
 *   - state.scatterDensity (0.0–1.0, default 0.15 = 15% of tiles filled)
 *   - state.selectedTileId or state.stamp
 *   - state.randomTiles (picks randomly from set if available)
 *   - state.tileFlipFlags (flip/rotate)
 *   - Only places on empty tiles (value 0) by default — set scatterOverwrite to override
 */
export class ScatterTool {
  constructor(state, mapCanvas, commandHistory) {
    this.state = state;
    this.mapCanvas = mapCanvas;
    this.commandHistory = commandHistory;
    this._dragStart = null;
    this._dragEnd = null;
  }

  onDown(tx, ty) {
    this._dragStart = { x: tx, y: ty };
    this._dragEnd = { x: tx, y: ty };
    this.state.batch({
      selectionStart: { x: tx, y: ty },
      selectionEnd: { x: tx, y: ty },
    });
  }

  onMove(tx, ty) {
    if (!this._dragStart) return;
    this._dragEnd = { x: tx, y: ty };
    this.state.batch({
      selectionEnd: { x: tx, y: ty },
    });
  }

  onUp(tx, ty) {
    if (!this._dragStart) return;
    this._dragEnd = { x: tx, y: ty };

    // Apply scatter
    const changes = this._scatter();
    if (changes.length > 0) {
      const layerIndex = this.state.get('activeLayerIndex');
      const cmd = new PaintCommand(this.state, layerIndex, changes);
      this.commandHistory.push(cmd);
    }

    // Clear selection visual
    this._dragStart = null;
    this._dragEnd = null;
    this.state.batch({
      selectionStart: null,
      selectionEnd: null,
    });
  }

  _scatter() {
    const start = this._dragStart;
    const end = this._dragEnd;
    if (!start || !end) return [];

    const x1 = Math.max(0, Math.min(start.x, end.x));
    const y1 = Math.max(0, Math.min(start.y, end.y));
    const x2 = Math.min(this.state.get('mapWidth') - 1, Math.max(start.x, end.x));
    const y2 = Math.min(this.state.get('mapHeight') - 1, Math.max(start.y, end.y));

    const density = this.state.get('scatterDensity') ?? 0.15;
    const overwrite = this.state.get('scatterOverwrite') ?? false;
    const layerIndex = this.state.get('activeLayerIndex');
    const flipFlags = this.state.get('tileFlipFlags') || 0;

    // Determine what to scatter
    const randomTiles = this.state.get('randomTiles');
    const stamp = this.state.get('stamp');
    const selectedTileId = this.state.get('selectedTileId');

    const changes = [];

    for (let y = y1; y <= y2; y++) {
      for (let x = x1; x <= x2; x++) {
        if (Math.random() > density) continue;

        const oldTile = this.state.getTile(layerIndex, x, y);
        if (!overwrite && oldTile !== 0) continue;

        let tileId;
        if (randomTiles && randomTiles.length > 0) {
          tileId = randomTiles[Math.floor(Math.random() * randomTiles.length)];
        } else if (stamp) {
          // Pick a random tile from the stamp
          const row = stamp[Math.floor(Math.random() * stamp.length)];
          tileId = row[Math.floor(Math.random() * row.length)];
        } else {
          tileId = selectedTileId;
        }

        if (!tileId) continue;
        tileId = tileId | flipFlags;

        if (oldTile === tileId) continue;

        this.state.setTile(layerIndex, x, y, tileId);
        changes.push({ x, y, oldTile, newTile: tileId });
      }
    }

    if (changes.length > 0) {
      this.mapCanvas.invalidateLayer(layerIndex);
    }

    return changes;
  }
}
